import { Component, effect } from '@angular/core';
import { MessageService } from 'primeng/api';
import { PRIMENG_MODULES } from '../../shared/primeng-imports';
import { Table } from 'primeng/table';
import { ParcelService } from '../../shared/services/parcel.service';
import { DataService } from '../../shared/services/data.service';
import { ParcelDetailComponent } from '../parcel-detail/parcel-detail.component';
import { SpinnerComponent } from "../../shared/components/spinner/spinner.component";

type RetrievalStatus = 'AVAILABLE' | 'RESTORE_INITIATED' | 'RESTORE_REQUESTED' | 'FAILED';

interface RetrievalRequest {
    itemId: string;
    reason: string;
    requestedAt: string;
    requestedBy: string;
    availableUntil: string;
    status: RetrievalStatus;
}

@Component({
    selector: 'app-retrieval-requests',
    standalone: true,
    imports: [...PRIMENG_MODULES, ParcelDetailComponent, SpinnerComponent],
    templateUrl: './retrieval-requests.component.html',
    styleUrl: './retrieval-requests.component.scss'
})
export class RetrievalRequestsComponent {
    retrievalRequests: RetrievalRequest[] = [];
    filteredRetrievalRequests: RetrievalRequest[] = [];
    filterItemId = '';
    selectedStatuses: RetrievalStatus[] = [];
    readonly statusFilterOptions = [
        { label: 'User Initiated', value: 'RESTORE_INITIATED' },
        { label: 'In Progress', value: 'RESTORE_REQUESTED' },
        { label: 'Available', value: 'AVAILABLE' },
        { label: 'Failed', value: 'FAILED' }
    ];

    retrievalInitiateLoading: boolean = false;
    currentUser: any;
    selectedRow: any;
    displayDialog: boolean = false;
    recordNotFound: boolean = false;
    isLoading: boolean = false;
    loadingItemId: string | null = null;

    constructor(
        private parcelService: ParcelService,
        private messageService: MessageService,
        private dataService: DataService
    ) {
        effect(() => {
            this.currentUser = this.dataService.currentDataSignal();
            this.loadRequestsForLoggedInUser();
        });
    }

    loadRequestsForLoggedInUser() {
        this.isLoading = true;
        this.parcelService.listRetrievalRequests().subscribe({
            next: (result) => {
                const apiRows = Array.isArray(result) ? result : (result?.data || []);
                this.retrievalRequests = apiRows.map((row: any) => this.mapToRetrievalRequest(row)).filter((row: any) => row.status !== 'EXPIRED');
                this.updateFilteredSummaryRequests();
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Failed to load retrieval requests', err);
                this.retrievalRequests = [];
                this.updateFilteredSummaryRequests();
                this.isLoading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load retrieval requests. Please try again later.'
                });
            }
        });
    }

    refreshRequests() {
        this.loadRequestsForLoggedInUser();
        this.updateFilteredSummaryRequests();
    }

    applyTableFilters(table: Table) {
        table.filter(this.filterItemId, 'itemId', 'contains');
        table.filter(this.selectedStatuses?.length ? this.selectedStatuses : null, 'status', 'in');
        this.updateFilteredSummaryRequests();
    }

    clearTableFilters(table: Table) {
        this.filterItemId = '';
        this.selectedStatuses = [];
        table.clear();
        this.updateFilteredSummaryRequests();
    }

    private mapToRetrievalRequest(row: any): RetrievalRequest {

        return {
            itemId: row?.itemId || row?.itemID || '',
            reason: row?.reason || '',
            requestedAt: row?.requestedAt || row?.requestedAt || row?.createdAt || '',
            requestedBy: row?.requestedBy || row?.requestedUser || '',
            availableUntil: row?.availableUntil || row?.expiryDate || '',
            status: row?.status || ''
        };
    }

    private updateFilteredSummaryRequests() {
        const normalizedItemId = this.filterItemId.trim().toLowerCase();
        const normalizedStatuses = this.selectedStatuses.map((status) => status.toLowerCase());

        this.filteredRetrievalRequests = this.retrievalRequests.filter((request) => {
            const matchesItemId = !normalizedItemId || request.itemId.toLowerCase().includes(normalizedItemId);
            const matchesStatus =
                normalizedStatuses.length === 0 || normalizedStatuses.includes(request.status.toLowerCase());

            return matchesItemId && matchesStatus;
        });
    }

    getTotalRequestsCount(): number {
        return this.filteredRetrievalRequests.length;
    }

    getRequestsCountByStatus(status: RetrievalStatus): number {
        return this.filteredRetrievalRequests.filter(
            (request) => request.status.toLowerCase() === status.toLowerCase()
        ).length;
    }

    getStatusIndicatorClass(status: RetrievalStatus): string {
        switch (status) {
            case 'AVAILABLE':
                return 'status-available';
            case 'RESTORE_REQUESTED':
                return 'status-inprogress';
            case 'RESTORE_INITIATED':
                return 'status-requested';
            case 'FAILED':
                return 'status-failed';
            default:
                return 'status-default';
        }
    }

    getStatusDisplayText(status: RetrievalStatus): string {
        if (status === 'RESTORE_REQUESTED') {
            return 'In Progress';
        }

        if (status === 'RESTORE_INITIATED') {
            return 'User Initiated';
        }

        if (status === 'AVAILABLE') {
            return 'Available';
        }

        if (status === 'FAILED') {
            return 'Failed';
        }
        return status;
    }

    canInitiateRetrieval(status: RetrievalStatus): boolean {
        return status === 'RESTORE_INITIATED';
    }

    canOpenParcelDetails(status: RetrievalStatus): boolean {
        return status === 'AVAILABLE';
    }

    onItemIdClick(request: RetrievalRequest): void {
        if (!this.canOpenParcelDetails(request.status)) {
            return;
        }

        this.recordNotFound = false;
        this.selectedRow = request;
        this.displayDialog = true;
    }

    handleRecordNotFound() {
        this.recordNotFound = true;
    }

    resetModalState() {
        this.recordNotFound = false;
        this.selectedRow = null;
    }

    initiateRetrieval(request: RetrievalRequest) {
        if (!this.canInitiateRetrieval(request.status)) {
            return;
        }

        this.retrievalInitiateLoading = true;
        this.loadingItemId = request.itemId;

        const requestData = {
            itemId: request.itemId,
            restoredBy: this.currentUser,
        };
        this.parcelService.initiateRetrievalRequest(requestData).subscribe({
            next: (result) => {
                this.retrievalInitiateLoading = false;
                this.loadingItemId = null;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Your retrieval has been initiated successfully. Please wait 5-10 minutes for the process to complete',
                    life: 6000
                });
                request.status = 'RESTORE_REQUESTED';
            },
            error: (err) => {
                this.retrievalInitiateLoading = false;
                this.loadingItemId = null;
                console.error('Failed to submit retrieval request', err);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to initiate request. Please try again later.'
                });
                request.status = 'FAILED';
            }
        });
    }
}
